<template>
  <div id="apt-favorite" class="apt-favorite-wrapper">
    <div class="page-header">
      <div class="title">관심 아파트</div>
      <div class="subtitle">등록한 아파트의 면적대별 거래 추이와 최근 실거래를 확인합니다</div>
    </div>

    <!-- 관심 아파트 탭 -->
    <div class="favorite-tabs" v-if="favorites.length > 0">
      <div
        v-for="(fav, idx) in favorites"
        :key="fav.id"
        class="tab-item"
        :class="{ active: selectedIdx === idx }"
        draggable="true"
        @dragstart="onDragStart(idx, $event)"
        @dragover.prevent
        @dragenter.prevent="onDragEnter(idx)"
        @drop="onDrop(idx)"
        @click="selectTab(idx)"
      >
        <span class="tab-order">{{ idx + 1 }}</span>
        <span class="tab-name">{{ fav.aptNm }}</span>
        <button class="tab-delete-btn" @click.stop="onDeleteFavorite(fav, idx)" title="삭제">&times;</button>
      </div>
    </div>

    <!-- 관심 아파트가 없을 때 -->
    <div class="empty-state" v-if="!loading && favorites.length === 0">
      <div class="empty-icon">&#127968;</div>
      <p>등록된 관심 아파트가 없습니다.</p>
      <p class="empty-sub">아파트 실거래가 검색에서 관심 아파트를 추가해 보세요.</p>
    </div>

    <!-- 로딩 -->
    <div class="loading-state" v-if="loading">
      <p>불러오는 중...</p>
    </div>

    <!-- 선택된 아파트 상세 -->
    <div class="favorite-detail" v-if="selectedFavorite && !loading">
      <PeriodSelector :selected="period" @change="onChangePeriod" />
      <FavoriteChartItem
        :key="selectedFavorite.id + '-' + period"
        :apt="selectedFavorite"
        :period="period"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import PeriodSelector from "../components/PeriodSelector.vue";
import FavoriteChartItem from "../components/FavoriteChartItem.vue";
import { AptFavorite } from "../class";

export default defineComponent({
  name: "AptFavoriteView",
  components: { PeriodSelector, FavoriteChartItem },
  setup() {
    return {};
  },

  data() {
    return {
      period: "1Y",
      favorites: [] as AptFavorite[],
      selectedIdx: 0,
      loading: false,
      dragFromIdx: -1,
    };
  },

  mixins: [UtilMixin],
  computed: {
    selectedFavorite(): AptFavorite | null {
      if (this.favorites.length === 0) return null;
      return this.favorites[this.selectedIdx] || null;
    },
  },
  methods: {
    async fetchFavorite() {
      this.loading = true;
      try {
        const res = await this.getApi().apt().getAptFavorite();
        this.favorites = res || [];
        this.selectedIdx = 0;
      } catch {
        this.favorites = [];
      } finally {
        this.loading = false;
      }
    },
    onChangePeriod(p: string) {
      this.period = p;
    },
    selectTab(idx: number) {
      this.selectedIdx = idx;
    },
    // 드래그 앤 드롭으로 탭 순서 변경
    onDragStart(idx: number, e: DragEvent) {
      this.dragFromIdx = idx;
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = "move";
      }
    },
    async onDeleteFavorite(fav: AptFavorite, idx: number) {
      if (!confirm(`${fav.aptNm}을(를) 관심 아파트에서 삭제하시겠습니까?`)) return;
      try {
        await this.getApi().apt().deleteAptFavorite(fav.id);
        this.favorites.splice(idx, 1);
        if (this.selectedIdx >= this.favorites.length) {
          this.selectedIdx = Math.max(0, this.favorites.length - 1);
        }
      } catch {
        alert("삭제에 실패했습니다.");
      }
    },
    onDragEnter(idx: number) {
      // visual feedback handled by CSS
    },
    onDrop(toIdx: number) {
      if (this.dragFromIdx < 0 || this.dragFromIdx === toIdx) return;
      const moved = this.favorites.splice(this.dragFromIdx, 1)[0];
      this.favorites.splice(toIdx, 0, moved);
      // 선택된 탭이 이동한 탭이면 따라가기
      if (this.selectedIdx === this.dragFromIdx) {
        this.selectedIdx = toIdx;
      } else if (
        this.dragFromIdx < this.selectedIdx &&
        toIdx >= this.selectedIdx
      ) {
        this.selectedIdx--;
      } else if (
        this.dragFromIdx > this.selectedIdx &&
        toIdx <= this.selectedIdx
      ) {
        this.selectedIdx++;
      }
      this.dragFromIdx = -1;
    },
  },

  created() {
    this.fetchFavorite();
  },
});
</script>
